import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { MessageSquare, Heart, Users, Star, FileText, Video, Clock } from 'lucide-react';
import { formatRelativeTime } from '../../utils/formatters';
import Avatar from '../common/Avatar';

interface Notification {
  id: string;
  type: 'comment' | 'like' | 'follow' | 'mention' | 'article' | 'video';
  title: string;
  description: string;
  user: {
    name: string;
    avatar: string;
  };
  createdAt: Date;
  read: boolean;
  link: string;
}

interface NotificationDropdownProps {
  notifications: Notification[];
  onClose: () => void;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
}

export default function NotificationDropdown({
  notifications,
  onClose,
  onMarkAsRead,
  onMarkAllAsRead
}: NotificationDropdownProps) {
  const { theme } = useTheme();

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'comment':
        return <MessageSquare className="h-5 w-5 text-blue-500" />;
      case 'like':
        return <Heart className="h-5 w-5 text-red-500" />;
      case 'follow':
        return <Users className="h-5 w-5 text-green-500" />;
      case 'mention':
        return <Star className="h-5 w-5 text-yellow-500" />;
      case 'article':
        return <FileText className="h-5 w-5 text-purple-500" />;
      case 'video':
        return <Video className="h-5 w-5 text-pink-500" />;
    }
  };

  return (
    <div className={`
      absolute right-0 mt-2 w-80 rounded-lg shadow-lg py-2 z-50
      ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
    `}>
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700">
        <h3 className={`font-semibold ${
          theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
        }`}>
          Notifications
        </h3>
        <button
          onClick={onMarkAllAsRead}
          className={`text-sm ${
            theme === 'dark'
              ? 'text-blue-400 hover:text-blue-300'
              : 'text-blue-600 hover:text-blue-700'
          }`}
        >
          Mark all as read
        </button>
      </div>

      <div className="max-h-[400px] overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="p-4 text-center">
            <Clock className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
              No new notifications
            </p>
          </div>
        ) : (
          notifications.map(notification => (
            <div
              key={notification.id}
              className={`
                relative px-4 py-3 transition-colors cursor-pointer
                ${notification.read
                  ? ''
                  : theme === 'dark'
                    ? 'bg-gray-700/50'
                    : 'bg-blue-50'
                }
                hover:${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}
              `}
              onClick={() => {
                if (!notification.read) {
                  onMarkAsRead(notification.id);
                }
                onClose();
                // Navigate to notification.link
              }}
            >
              <div className="flex items-start space-x-3">
                <Avatar
                  src={notification.user.avatar}
                  alt={notification.user.name}
                  size="sm"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    {getNotificationIcon(notification.type)}
                    <p className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                    }`}>
                      {notification.title}
                    </p>
                  </div>
                  <p className={`text-sm line-clamp-2 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {notification.description}
                  </p>
                  <p className={`text-xs mt-1 ${
                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                  }`}>
                    {formatRelativeTime(notification.createdAt)}
                  </p>
                </div>
              </div>
              {!notification.read && (
                <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-blue-500" />
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}